import { Controller } from "@hotwired/stimulus"

/**
 * Scroll Fade Controller
 *
 * Shows/hides gradient fade overlays on a horizontally scrollable container
 * based on scroll position. Hides left fade at start, right fade at end.
 */
export default class extends Controller {
  static targets = ["scroller", "fadeLeft", "fadeRight"]
  static values = { center: Boolean }

  declare scrollerTarget: HTMLElement
  declare fadeLeftTarget: HTMLElement
  declare fadeRightTarget: HTMLElement
  declare centerValue: boolean

  private resizeObserver?: ResizeObserver
  private userScrolled = false

  connect() {
    const el = this.scrollerTarget
    el.addEventListener("wheel", this.markUserScrolled, { passive: true })
    el.addEventListener("touchstart", this.markUserScrolled, { passive: true })
    el.addEventListener("keydown", this.markUserScrolled)

    if (this.centerValue) {
      requestAnimationFrame(() => {
        this.center()
        requestAnimationFrame(() => {
          this.hydrateVisibleImages()
          this.update()
        })
      })
    } else {
      this.update()
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (this.centerValue && !this.userScrolled) {
        this.center()
        this.hydrateVisibleImages()
      }
      this.update()
    })
    this.resizeObserver.observe(el)
  }

  disconnect() {
    const el = this.scrollerTarget
    el.removeEventListener("wheel", this.markUserScrolled)
    el.removeEventListener("touchstart", this.markUserScrolled)
    el.removeEventListener("keydown", this.markUserScrolled)
    this.resizeObserver?.disconnect()
  }

  private markUserScrolled = () => {
    this.userScrolled = true
  }

  /** Pin horizontal scroll to the midpoint so the strip opens full-bleed. */
  private center() {
    const el = this.scrollerTarget
    const overflow = el.scrollWidth - el.clientWidth
    if (overflow <= 1) return

    el.scrollLeft = overflow / 2
  }

  /** Programmatic scroll does not always wake lazy imgs — promote in-view ones. */
  private hydrateVisibleImages() {
    const root = this.scrollerTarget.getBoundingClientRect()
    const slack = 48

    this.scrollerTarget.querySelectorAll<HTMLImageElement>("img[loading='lazy']").forEach((img) => {
      const box = img.getBoundingClientRect()
      if (box.right < root.left - slack || box.left > root.right + slack) return

      img.loading = "eager"
      if (!img.complete) {
        const src = img.currentSrc || img.src
        if (src) img.src = src
      }
    })
  }

  update() {
    const el = this.scrollerTarget
    const atStart = el.scrollLeft <= 1
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1

    this.fadeLeftTarget.classList.toggle("hidden", atStart)
    this.fadeRightTarget.classList.toggle("hidden", atEnd)
  }
}
